import React, { useRef, useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    makeStyles,
    Typography,
} from '@material-ui/core'
import {
    Close as CloseIcon,
} from '@material-ui/icons'

import { useSpidTableQuery, useUserTableQuery, useTenantQuery } from 'components/api'
import UserReport from './UserReport'
import ManageEmails from './ManageEmails'
import './admin.scss'

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: '#f4f6f8',
        minHeight: 'calc(100vh - 64px)',
        padding: '40px 24px 80px',
    },
    header: {
        textAlign: 'center',
        marginBottom: 32,
    },
    tenantTitle: {
        fontSize: '1.55rem',
        fontWeight: 400,
        marginBottom: 6,
        color: '#222',
    },
    subtitle: {
        fontSize: '1.05rem',
        fontWeight: 600,
        color: '#e05050',
        margin: 0,
    },
    statsRow: {
        justifyContent: 'center',
        marginBottom: 36,
    },
    statCard: {
        backgroundColor: '#fff',
        border: '1px solid #d8dee6',
        borderRadius: 8,
        padding: '20px 28px',
        textAlign: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,.06)',
    },
    statNumber: {
        fontSize: '2.4rem',
        fontWeight: 700,
        lineHeight: 1,
        marginBottom: 6,
        display: 'block',
    },
    statLabel: {
        fontSize: '0.72rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#6b7585',
        display: 'block',
    },
    actionCards: {
        justifyContent: 'center',
        marginBottom: 28,
    },
    actionCard: {
        backgroundColor: '#fff',
        border: '1px solid #d8dee6',
        borderRadius: 8,
        padding: '28px 22px 24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,.06)',
        transition: 'box-shadow .15s',
        height: '100%',
        '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,.1)',
        },
    },
    actionTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#00b4c8',
        marginBottom: 12,
    },
    actionDesc: {
        fontSize: '0.87rem',
        color: '#555',
        lineHeight: 1.55,
        flex: 1,
        marginBottom: 20,
    },
    actionBtn: {
        fontWeight: 700,
        color: '#fff',
        width: '100%',
    },
    panel: {
        backgroundColor: '#fff',
        border: '1px solid #d8dee6',
        borderRadius: 10,
        marginTop: 32,
        boxShadow: '0 2px 8px rgba(0,0,0,.07)',
        overflow: 'hidden',
    },
    panelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 20px',
        backgroundColor: '#f8fafb',
        borderBottom: '1px solid #d8dee6',
    },
    panelTitle: {
        fontWeight: 600,
        fontSize: '1rem',
    },
    panelBody: {
        padding: 20,
    },
    statSkeleton: {
        display: 'inline-block',
        width: 48,
        height: 38,
        backgroundColor: '#eef0f4',
        borderRadius: 4,
    },
}))

function StatCard({ icon, number, label, color, loading }) {
    const classes = useStyles()
    return (
        <Grid item xs={12} sm={4} md={3}>
            <div className={classes.statCard}>
                {loading
                    ? <span className={classes.statSkeleton} />
                    : <span className={classes.statNumber} style={{ color }}>{number}</span>
                }
                <span className={classes.statLabel}>{label}</span>
            </div>
        </Grid>
    )
}

function ActionCard({ title, description, buttonLabel, buttonColor, onClick }) {
    const classes = useStyles()
    return (
        <Grid item xs={12} sm={6} md={3}>
            <div className={classes.actionCard}>
                <div className={classes.actionTitle}>{title}</div>
                <div className={classes.actionDesc}>{description}</div>
                <Button
                    variant="contained"
                    className={classes.actionBtn}
                    style={{ backgroundColor: buttonColor }}
                    onClick={onClick}
                    disableElevation
                >
                    {buttonLabel}
                </Button>
            </div>
        </Grid>
    )
}

export default function AdminDashboard() {
    const classes = useStyles()
    const [activeSection, setActiveSection] = useState(null)
    const panelRef = useRef(null)

    const { data: tenantData, isLoading: tenantLoading } = useTenantQuery()
    const { data: userData, isLoading: userLoading } = useUserTableQuery()
    const { data: spidData, isLoading: spidLoading } = useSpidTableQuery()

    const statsLoading = userLoading || spidLoading

    const activeUsers = Array.isArray(userData)
        ? userData.filter(u =>
            u.Enabled && u['custom:is_super_admin'] !== 'True'
          ).length
        : 0
    const activeSpids = Array.isArray(spidData)
        ? spidData.filter(s => s.state !== 'Deactivated').length
        : 0
    const offboardedSpids = Array.isArray(spidData)
        ? spidData.filter(s => s.state === 'Deactivated').length
        : 0

    const tenantName = tenantData?.tenant_name || ''

    const openSection = (section) => {
        setActiveSection(prev => prev === section ? null : section)
    }

    useEffect(() => {
        if (activeSection && panelRef.current) {
            setTimeout(() => {
                panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 50)
        }
    }, [activeSection])

    return (
        <div className={classes.root}>
            <Grid container>

                {/* Header */}
                <Grid item xs={12} className={classes.header}>
                    {tenantLoading
                        ? <CircularProgress size={20} />
                        : <Typography className={classes.tenantTitle}>
                            {tenantName ? `Welcome to the ${tenantName} Admin Hub` : 'Admin Hub'}
                          </Typography>
                    }
                    <p className={classes.subtitle}>Please select an action or view below:</p>
                </Grid>

                {/* Stats row */}
                <Grid item xs={12}>
                    <Grid container spacing={2} className={classes.statsRow}>
                        <StatCard
                            label="Active Users"
                            number={activeUsers}
                            color="#00b4c8"
                            loading={statsLoading}
                        />
                        <StatCard
                            label="Active SPIDs"
                            number={activeSpids}
                            color="#7b5ea7"
                            loading={statsLoading}
                        />
                        <StatCard
                            label="Offboarded SPIDs"
                            number={offboardedSpids}
                            color="#e05050"
                            loading={statsLoading}
                        />
                    </Grid>
                </Grid>

                {/* Action cards */}
                <Grid item xs={12}>
                    <Grid container spacing={2} className={classes.actionCards}>
                        <ActionCard
                            title="View User Report"
                            description="Generate a list of all currently active users in your organization for audit and compliance purposes."
                            buttonLabel="Generate Report"
                            buttonColor="#00b4c8"
                            onClick={() => openSection('report')}
                        />
                        <ActionCard
                            title="Email Templates"
                            description="Browse and preview the email templates configured for your organization across all languages and event types."
                            buttonLabel="View Emails"
                            buttonColor="#e8a020"
                            onClick={() => openSection('emails')}
                        />
                    </Grid>
                </Grid>

                {/* Active panel */}
                {activeSection && (
                    <Grid item xs={12} ref={panelRef}>
                        <div className={classes.panel}>
                            <div className={classes.panelHeader}>
                                <Typography className={classes.panelTitle}>
                                    {activeSection === 'report' ? 'Active User Report' : 'Email Templates'}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setActiveSection(null)}
                                    aria-label="close panel"
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </div>
                            <div className={classes.panelBody}>
                                {activeSection === 'report' && <UserReport />}
                                {activeSection === 'emails' && <ManageEmails />}
                            </div>
                        </div>
                    </Grid>
                )}

            </Grid>
        </div>
    )
}
